import { useEffect, useRef } from 'react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import {
  useOpInfiniteQuery,
  UseOpInfiniteQueryOptions,
} from 'utils/customHooks/useOpInfiniteQuery';
import { GetterApiEndpointNames } from 'new-components/DLS/OpDataFetchTable/types';

export const useFetchAllViaCursor = <
  T extends GetterApiEndpointNames,
  SelectData = InfiniteData<Awaited<ReturnType<Api.Client[T]>>>,
>(
  opInfiniteQueryProps: UseOpInfiniteQueryOptions<T, SelectData>,
) => {
  const queryClient = useQueryClient();
  const lastCursor = useRef('');
  const totalData = useRef<Awaited<ReturnType<Api.Client[T]>>['json']['data']>(
    [],
  );

  const result = useOpInfiniteQuery({
    ...opInfiniteQueryProps,
    gcTime: 0, // Garbage collect the query when ASAP
    maxPages: 1, // Only keep the latest page in the cache
    select: (data) => {
      const latestPage = data.pages[data.pages.length - 1];
      const latestPageData = latestPage.json.data;
      const latestPageCursor = latestPage.json.cursors?.nextCursor || '';

      // If we're processing a new page or this is the first data load
      if (
        lastCursor.current !== latestPageCursor ||
        totalData.current.length === 0
      ) {
        // Update the last cursor
        lastCursor.current = latestPageCursor;

        // Add the latest page data to the total data
        totalData.current = [...totalData.current, ...latestPageData];
      }

      // Return the data as is
      return data as SelectData;
    },
  });

  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFetching,
    isError,
  } = result;

  // Automatically fetch the next results if there are more to get
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isError]);

  // Cleanup when fetching is complete
  useEffect(() => {
    if (!isFetching && !hasNextPage) {
      lastCursor.current = '';
      totalData.current = [];
      queryClient.removeQueries({ queryKey: opInfiniteQueryProps.queryKey });
    }
  }, [hasNextPage, isFetching, opInfiniteQueryProps.queryKey, queryClient]);

  return {
    ...result,
    totalData,
  };
};
